import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, BadgeCheck, Check, Clock, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FadeUp } from "@/components/Reveal";
import { trackLeadConversion } from "@/lib/googleAds";
import { CONTACT, usePageTitle } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEL = `tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`;

const inputCls = "h-12 rounded-xl border-black/15 bg-white text-base focus-visible:ring-pine";

const Field = ({ label, htmlFor, children }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
      {label}
    </Label>
    {children}
  </div>
);

function ServiceLeadForm({ cfg }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        organization: form.business || null,
        client_type: "Small business",
        service: cfg.serviceValue,
        message: form.message,
      });
      trackLeadConversion(res.data?.lead_id);
      setDone(true);
      toast.success("Request received — we will be in touch within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${cfg.prefix}-form-success`} className="flex flex-col items-start gap-4 py-6">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-pine/10">
          <Check className="h-6 w-6 text-pine" />
        </span>
        <h3 className="font-serif text-2xl tracking-tight text-ink">Request received.</h3>
        <p className="text-sm leading-relaxed text-zinc-600">
          Thank you, {form.name.split(" ")[0]}. A confirmation is on its way to your inbox, and we
          will reach out within one business day with next steps and a fixed-fee quote.
        </p>
        <p className="text-sm text-zinc-600">
          Prefer to talk now?{" "}
          <a href={TEL} className="font-semibold text-pine transition-colors hover:text-clay">
            Call {CONTACT.phone}
          </a>
        </p>
      </div>
    );
  }

  return (
    <form data-testid={`${cfg.prefix}-lead-form`} onSubmit={submit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name *" htmlFor={`${cfg.prefix}-name`}>
          <Input id={`${cfg.prefix}-name`} data-testid={`${cfg.prefix}-name-input`} required minLength={2} value={form.name} onChange={set("name")} placeholder="Jordan Miller" className={inputCls} />
        </Field>
        <Field label="Email *" htmlFor={`${cfg.prefix}-email`}>
          <Input id={`${cfg.prefix}-email`} data-testid={`${cfg.prefix}-email-input`} required type="email" value={form.email} onChange={set("email")} placeholder="you@business.com" className={inputCls} />
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Phone" htmlFor={`${cfg.prefix}-phone`}>
          <Input id={`${cfg.prefix}-phone`} data-testid={`${cfg.prefix}-phone-input`} type="tel" value={form.phone} onChange={set("phone")} placeholder="(206) 555-0100" className={inputCls} />
        </Field>
        <Field label="Organization" htmlFor={`${cfg.prefix}-biz`}>
          <Input id={`${cfg.prefix}-biz`} data-testid={`${cfg.prefix}-business-input`} value={form.business} onChange={set("business")} placeholder="Business or tribe name" className={inputCls} />
        </Field>
      </div>
      <Field label={cfg.messageLabel} htmlFor={`${cfg.prefix}-msg`}>
        <Textarea
          id={`${cfg.prefix}-msg`}
          data-testid={`${cfg.prefix}-message-input`}
          required
          minLength={10}
          rows={4}
          value={form.message}
          onChange={set("message")}
          placeholder={cfg.formPlaceholder}
          className="rounded-xl border-black/15 bg-white text-base focus-visible:ring-pine"
        />
      </Field>
      <Button
        type="submit"
        data-testid={`${cfg.prefix}-submit-button`}
        disabled={sending}
        className="w-full rounded-full bg-clay py-6 text-sm font-semibold text-white transition-colors duration-300 hover:bg-pine"
      >
        {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {sending ? "Sending…" : cfg.submitLabel}
      </Button>
      <p className="text-xs leading-relaxed text-zinc-500">
        Free, no obligation. We reply within one business day.
      </p>
    </form>
  );
}

export const ServiceLanding = ({ cfg }) => {
  usePageTitle(cfg.pageTitle);

  return (
    <>
      {/* ————— Phone-visible top bar ————— */}
      <div data-testid={`${cfg.prefix}-topbar`} className="bg-pine pt-20 text-paper">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-6 py-3 lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-paper/70 sm:text-sm">
            {cfg.topbarLabel}
          </p>
          <a
            href={TEL}
            data-testid={`${cfg.prefix}-topbar-phone`}
            className="group inline-flex items-center gap-2 text-sm font-bold tracking-wide transition-colors hover:text-clay sm:text-base"
          >
            <Phone className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
            {CONTACT.phone}
          </a>
        </div>
      </div>

      {/* ————— Hero + form ————— */}
      <section data-testid={`${cfg.prefix}-hero`} className="border-b border-black/10 pb-20 pt-14 lg:pb-24 lg:pt-16">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-7">
            <FadeUp>
              <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
                <span className="h-px w-10 bg-pine" />
                {cfg.eyebrow}
              </p>
              <h1 className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tighter text-ink sm:text-6xl">
                {cfg.heroTitle}
              </h1>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg">
                {cfg.heroSub}
              </p>
              <div data-testid={`${cfg.prefix}-trust-strip`} className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
                  <BadgeCheck className="h-5 w-5 text-pine" />
                  15 years in practice
                </span>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
                  <Clock className="h-5 w-5 text-pine" />
                  Replies within 1 business day
                </span>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
                  <Check className="h-5 w-5 text-pine" />
                  Fixed-fee engagements
                </span>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href={TEL}
                  data-testid={`${cfg.prefix}-call-button`}
                  className="group inline-flex items-center gap-2 rounded-full bg-pine px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
                >
                  <Phone className="h-4 w-4" />
                  Call {CONTACT.phone}
                </a>
                <a
                  href={`#${cfg.prefix}-form`}
                  data-testid={`${cfg.prefix}-quote-button`}
                  className="group inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:border-pine hover:text-pine"
                >
                  {cfg.quoteCta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-5" id={`${cfg.prefix}-form`}>
            <FadeUp delay={0.15}>
              <div className="rounded-3xl border border-black/10 bg-white p-8 sm:p-9 lg:sticky lg:top-28">
                <h2 className="font-serif text-2xl tracking-tight text-ink">{cfg.formHeading}</h2>
                <p className="mt-2 text-sm text-zinc-600">{cfg.formSub}</p>
                <div className="mt-6">
                  <ServiceLeadForm cfg={cfg} />
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ————— Who it's for ————— */}
      <section data-testid={`${cfg.prefix}-audience`} className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
              <span className="h-px w-10 bg-pine" />
              Who it's for
            </p>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
              {cfg.audienceHeading}
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-black/10 bg-black/10 sm:grid-cols-3">
            {cfg.audiences.map((a, i) => (
              <FadeUp key={a.title} delay={i * 0.08} className="bg-white">
                <div data-testid={`${cfg.prefix}-audience-${i + 1}`} className="h-full p-9 transition-colors duration-300 hover:bg-paper">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-pine/10">
                    <a.icon className="h-5 w-5 text-pine" />
                  </span>
                  <h3 className="mt-5 font-serif text-xl tracking-tight text-ink">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-600">{a.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ————— What's included ————— */}
      <section data-testid={`${cfg.prefix}-included`} className="border-y border-black/10 bg-white py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
                <span className="h-px w-10 bg-pine" />
                What's included
              </p>
              <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
                {cfg.includedHeading}
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-600">
                {cfg.includedSub}
              </p>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <FadeUp delay={0.1}>
              <ul className="grid gap-x-10 gap-y-4 sm:grid-cols-2">
                {cfg.included.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-zinc-700 sm:text-base">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-pine/10">
                      <Check className="h-3 w-3 text-pine" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ————— Social proof ————— */}
      <section data-testid={`${cfg.prefix}-testimonials`} className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
              <span className="h-px w-10 bg-pine" />
              Client stories
            </p>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
              {cfg.testimonialsHeading}
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-black/10 bg-black/10 lg:grid-cols-3">
            {cfg.testimonials.map((t, i) => (
              <FadeUp key={t.by} delay={i * 0.08} className="bg-white">
                <figure data-testid={`${cfg.prefix}-testimonial-${i + 1}`} className="flex h-full flex-col justify-between p-9 transition-colors duration-300 hover:bg-paper">
                  <blockquote className="font-serif text-lg leading-relaxed tracking-tight text-ink">
                    <span className="mb-3 block font-serif text-4xl italic leading-none text-pine/25">&ldquo;</span>
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-7">
                    <p className="text-sm font-semibold text-ink">{t.by}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.18em] text-zinc-500">{t.where}</p>
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Final CTA ————— */}
      <section data-testid={`${cfg.prefix}-final-cta`} className="bg-pine py-20 text-paper lg:py-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <FadeUp>
            <h2 className="font-serif text-4xl leading-tight tracking-tighter sm:text-5xl">
              {cfg.finalTitle}
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-paper/80">
              {cfg.finalSub}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href={`#${cfg.prefix}-form`}
                data-testid={`${cfg.prefix}-final-form-button`}
                className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-semibold text-pine transition-colors duration-300 hover:bg-clay hover:text-white"
              >
                {cfg.quoteCta}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href={TEL}
                data-testid={`${cfg.prefix}-final-call-button`}
                className="group inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-4 text-sm font-semibold text-paper transition-colors duration-300 hover:border-white hover:bg-white/10"
              >
                <Phone className="h-4 w-4" />
                {CONTACT.phone}
              </a>
            </div>
            <p className="mt-8 text-sm text-paper/60">
              Prefer email?{" "}
              <Link to="/contact" data-testid={`${cfg.prefix}-contact-link`} className="font-semibold text-paper underline underline-offset-4 transition-colors hover:text-clay">
                Send us a message
              </Link>{" "}
              or browse{" "}
              <Link to="/services" data-testid={`${cfg.prefix}-services-link`} className="font-semibold text-paper underline underline-offset-4 transition-colors hover:text-clay">
                all services
              </Link>
              .
            </p>
          </FadeUp>
        </div>
      </section>
    </>
  );
};
